import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import PageHero from '../components/PageHero';
import { fadeUp, stagger, viewportOpts } from '../hooks/useScrollAnimation';
import { galleryImages } from '../data/gallery';

const Wave = ({ fill, bg = "transparent" }) => (
  <div className="wave-divider" style={{ background: bg }}>
    <svg viewBox="0 0 1440 70" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none" style={{ height: 50 }}>
      <path d="M0,35 C200,70 400,0 720,35 C1040,70 1280,5 1440,35 L1440,70 L0,70 Z" fill={fill} />
    </svg>
  </div>
);

const categories = [
  "All",
  "Classroom Activities",
  "Annual Day",
  "Festival Celebrations",
  "Sports Day",
  "Art and Craft",
  "Field Trips",
  "Graduation Ceremony",
  "Summer Camp"
];

export default function Gallery() {
  const [activeCategory, setActiveCategory] = useState("All");
  const [lightboxIndex, setLightboxIndex] = useState(null);

  const filteredImages = activeCategory === "All" 
    ? galleryImages 
    : galleryImages.filter(img => img.category === activeCategory);

  const openLightbox = (index) => setLightboxIndex(index);
  const closeLightbox = () => setLightboxIndex(null);

  const showPrev = (e) => {
    e.stopPropagation();
    setLightboxIndex(prev => (prev - 1 + filteredImages.length) % filteredImages.length);
  };

  const showNext = (e) => {
    e.stopPropagation();
    setLightboxIndex(prev => (prev + 1) % filteredImages.length);
  };

  // Keyboard navigation for lightbox
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (lightboxIndex === null) return;
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowLeft') setLightboxIndex(prev => (prev - 1 + filteredImages.length) % filteredImages.length);
      if (e.key === 'ArrowRight') setLightboxIndex(prev => (prev + 1) % filteredImages.length);
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxIndex, filteredImages]);

  return (
    <div>
      <PageHero 
        title="Life at Happykidz" 
        subtitle="Explore joyful moments from classroom learning, celebrations, sports, creative activities, field experiences, annual events, and student milestones." 
        breadcrumb="Gallery" 
      />

      {/* Gallery Main */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Filter Tabs */}
          <div className="flex flex-wrap justify-center gap-2 mb-12">
            {categories.map(cat => (
              <button
                key={cat}
                onClick={() => { setActiveCategory(cat); setLightboxIndex(null); }}
                className={`px-5 py-2 rounded-full font-nunito font-bold text-sm transition-all duration-200 ${
                  activeCategory === cat 
                    ? 'bg-[#C8161D] text-white shadow-md' 
                    : 'bg-[#F8FAFC] text-gray-600 hover:bg-gray-100'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>

          {/* Grid */}
          <motion.div 
            className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6"
            layout
            initial="hidden"
            whileInView="visible"
            viewport={viewportOpts}
            variants={stagger}
          >
            <AnimatePresence mode="popLayout">
              {filteredImages.map((img, idx) => (
                <motion.div
                  key={img.id}
                  layout
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ duration: 0.3 }}
                  className="group relative rounded-3xl overflow-hidden shadow-md cursor-pointer aspect-[4/3] bg-gray-100 border border-gray-50"
                  onClick={() => openLightbox(idx)}
                >
                  <img 
                    src={img.src} 
                    alt={img.alt} 
                    loading="lazy" 
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" 
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-5">
                    <div>
                      <span className="text-[#9DC41E] text-xs font-bold block mb-1 uppercase tracking-wider">{img.category}</span>
                      <h4 className="text-white font-nunito font-extrabold text-lg leading-tight">{img.title}</h4>
                    </div>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </motion.div>

          {/* Empty State */}
          {filteredImages.length === 0 && (
            <div className="text-center py-16 bg-[#F8FAFC] rounded-3rem border border-dashed border-gray-200">
              <p className="text-gray-400 text-base font-poppins">No images found in this category. Updates coming soon!</p>
            </div>
          )}
        </div>
      </section>

      {/* Accessible Lightbox Modal */}
      <AnimatePresence>
        {lightboxIndex !== null && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4"
            onClick={closeLightbox}
          >
            {/* Close Button */}
            <button 
              onClick={closeLightbox} 
              className="absolute top-6 right-6 w-11 h-11 bg-white/10 hover:bg-white/20 text-white rounded-full flex items-center justify-center transition-colors"
              aria-label="Close Lightbox"
            >
              <X size={20} />
            </button>

            {/* Prev Button */}
            <button 
              onClick={showPrev} 
              className="absolute left-6 w-12 h-12 bg-white/10 hover:bg-white/20 text-white rounded-full flex items-center justify-center transition-colors"
              aria-label="Previous Image"
            >
              <ChevronLeft size={24} />
            </button>

            {/* Active Image */}
            <motion.div 
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.95 }}
              className="max-w-4xl max-h-[80vh] flex flex-col items-center"
              onClick={(e) => e.stopPropagation()}
            >
              <img 
                src={filteredImages[lightboxIndex].src} 
                alt={filteredImages[lightboxIndex].alt} 
                className="max-w-full max-h-[70vh] rounded-2xl object-contain shadow-2xl" 
              />
              <div className="text-center mt-4 text-white">
                <span className="text-[#9DC41E] text-xs font-bold tracking-wider uppercase block mb-1">
                  {filteredImages[lightboxIndex].category}
                </span>
                <h4 className="font-nunito font-extrabold text-xl">
                  {filteredImages[lightboxIndex].title}
                </h4>
              </div>
            </motion.div>

            {/* Next Button */}
            <button 
              onClick={showNext} 
              className="absolute right-6 w-12 h-12 bg-white/10 hover:bg-white/20 text-white rounded-full flex items-center justify-center transition-colors"
              aria-label="Next Image"
            >
              <ChevronRight size={24} />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
